import React, { FocusEventHandler } from 'react';

// MUI
import { FormHelperText, Grid, InputLabel, TextField, TextFieldProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

// Formik
import { FieldHookConfig, useField } from 'formik';

// third-parties
import NumberFormat, { NumberFormatProps } from 'react-number-format';

// Components
import AutoComplete from './Autocomplete';

// types
import { BreakePoints } from 'types/breakepoints';

export type FormikTextInputProps = {
  label?: string;
  maskedInputProps?: Omit<NumberFormatProps, 'customInput' | 'size' | 'color'>;
  textInputProps?: TextFieldProps;
  breakePoints?: {
    label?: BreakePoints;
    input?: BreakePoints;
  };
} & FieldHookConfig<string>;

const FormikTextInput = (props: FormikTextInputProps) => {
  const [field, meta] = useField(props);
  const { id, label, placeholder, type, maskedInputProps, textInputProps } = props;
  const inputProps = { placeholder, type };

  const { breakePoints } = props;
  return (
    <Grid container>
      <Grid container alignItems="center">
        {label && (
          <Grid item xs={12} sm={3} {...breakePoints?.label}>
            <InputLabel htmlFor={id}>{label}:</InputLabel>
          </Grid>
        )}
        <Grid item xs={12} sm={label ? 9 : 12} {...breakePoints?.input}>
          {maskedInputProps ? (
            <NumberFormat
              {...field}
              {...maskedInputProps}
              customInput={TextField}
              InputProps={{
                error: Boolean(meta.touched && meta.error),
                placeholder: placeholder,
                ...textInputProps?.InputProps
              }}
              fullWidth
            />
          ) : (
            <TextField
              {...textInputProps}
              {...field}
              onChange={(e) => {
                field.onChange(e);
                textInputProps?.onChange?.(e);
              }}
              error={Boolean(meta.touched && meta.error)}
              fullWidth
              {...inputProps}
            />
          )}
        </Grid>
      </Grid>
      {Boolean(meta.touched && meta.error) && (
        <Grid container justifyContent="flex-end">
          <Grid item xs={12} sm={label ? 9 : 12} {...breakePoints?.input}>
            <FormHelperText error>{meta.error}</FormHelperText>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

interface FormikAutoCompleteInputProps<T extends object> {
  id: string;
  label: string;
  name: string;
  placeholder: string;
  options: T[];
  getOptionLabel?: (option: T) => string;
  value?: T | null;
  onChange?: (option: T | null) => void;
  onBlur?: FocusEventHandler<HTMLDivElement> | undefined;
  error?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  breakePoints?: {
    label?: BreakePoints;
    input?: BreakePoints;
  };
}

const FormikAutoCompleteInput = <T extends object>(props: FormikAutoCompleteInputProps<T>) => {
  const { onChange, onBlur, id, label, name, placeholder, error, errorMessage, breakePoints, ...others } = props;

  return (
    <Grid container>
      <Grid container alignItems="center">
        <Grid item xs={12} sm={3} {...breakePoints?.label}>
          <InputLabel htmlFor={id}>{label}:</InputLabel>
        </Grid>
        <Grid item xs={12} sm={9} {...breakePoints?.input}>
          <AutoComplete
            id={id}
            ListboxProps={{ style: { maxHeight: '10rem' } }}
            fullWidth
            onChange={(e, v) => onChange?.(v)}
            onBlur={onBlur}
            {...others}
            renderInput={(params) => <TextField {...params} name={name} placeholder={placeholder} error={error} />}
          />
        </Grid>
      </Grid>
      {error && (
        <Grid container justifyContent="flex-end">
          <Grid item xs={12} sm={9} {...breakePoints?.input}>
            <FormHelperText error>{errorMessage}</FormHelperText>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

interface FormikAutoCompleteProps<T extends {}> {
  breakePoints?: {
    label?: BreakePoints;
    input?: BreakePoints;
  };
  autoCompleteProps: {
    id: string;
    label?: string;
    placeholder?: string;
    options: T[];
    getOptionLabel?: (option: T) => string;
    getOptionKey?: (option: T) => string | number;
    onChange?: (value: T | null) => void;
    onOpen?: () => void;
    disableClearable?: boolean;
    disablePortal?: boolean;
    disabled?: boolean;
    multiple?: boolean;
  };
  textInputProps?: TextFieldProps;
}

const FormikAutoComplete = <T extends {}>({
  autoCompleteProps,
  breakePoints,
  ...props
}: FormikAutoCompleteProps<T> & FieldHookConfig<T | null>) => {
  const { id, label, placeholder, onChange, ...others } = autoCompleteProps;
  const { textInputProps } = props;
  const [field, meta, helpers] = useField(props);

  const { name, value } = field;
  const { setValue, setTouched } = helpers;
  const { touched, error } = meta;
  return (
    <Grid container>
      <Grid container alignItems="center">
        {label && (
          <Grid item xs={12} sm={3} {...breakePoints?.label}>
            <InputLabel htmlFor={id}>{label}:</InputLabel>
          </Grid>
        )}
        <Grid item xs={12} sm={label ? 9 : 12} {...breakePoints?.input}>
          <AutoComplete
            id={id}
            ListboxProps={{ style: { maxHeight: '10rem' } }}
            fullWidth
            onChange={(_, v) => {
              if (onChange) {
                onChange(v as T | null);
              } else {
                setValue(v as T | null);
              }
            }}
            onBlur={() => setTouched(true)}
            value={value}
            {...others}
            renderInput={(params) => (
              <TextField {...textInputProps} {...params} name={name} placeholder={placeholder} error={Boolean(error && touched)} />
            )}
          />
        </Grid>
      </Grid>
      {Boolean(touched && error) && (
        <Grid container justifyContent="flex-end">
          <Grid item xs={12} sm={label ? 9 : 12} {...breakePoints?.input}>
            <FormHelperText error>{error}</FormHelperText>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

interface FormikDateInputProps {
  label: string;
  dateOnly?: boolean;
  disableFuture?: boolean;
  breakePoints?: {
    label?: BreakePoints;
    input?: BreakePoints;
  };
}

const FormikDateInput = (props: FormikDateInputProps & FieldHookConfig<Date | null> & Partial<React.ComponentProps<typeof DatePicker>>) => {
  const [field, meta, helpers] = useField(props);
  const { id, label, placeholder, dateOnly = false, disableFuture = false, breakePoints, ...others } = props;
  const { setValue, setTouched } = helpers;

  const normalizeDate = (value: Date | null): Date | null => {
    if (!value) return null;

    if (!dateOnly) return value;

    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0, 0);
  };

  return (
    <Grid container>
      <Grid container alignItems="center">
        <Grid item xs={12} sm={3} {...breakePoints?.label}>
          <InputLabel htmlFor={id}>{label}:</InputLabel>
        </Grid>
        <Grid item xs={12} sm={9} {...breakePoints?.input}>
          <DatePicker
            {...field}
            onChange={(value) => {
              const normalized = normalizeDate(value as Date | null);
              setValue(normalized);
            }}
            disableFuture={disableFuture}
            renderInput={(params) => (
              <TextField
                fullWidth
                {...params}
                inputProps={{ ...params.inputProps, placeholder, onBlur: () => setTouched(true) }}
                error={!!(meta.error && meta.touched)}
              />
            )}
            {...others}
          />
        </Grid>
      </Grid>
      {Boolean(meta.touched && meta.error) && (
        <Grid container justifyContent="flex-end">
          <Grid item xs={12} sm={9} {...breakePoints?.input}>
            <FormHelperText error>{meta.error}</FormHelperText>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export { FormikTextInput, FormikAutoCompleteInput, FormikAutoComplete, FormikDateInput };
